'use strict';

angular.module('dashboard')
    .service('CampaignWizardService',
        [
            '$timeout',
            'LocalisationService',
            'PermissionService',
            'DashboardConfig',
            'moment',
            CampaignWizardService,
        ]);

function CampaignWizardService(
    $timeout,
    LocalisationService,
    PermissionService,
    DashboardConfig,
    moment
) {
    const self = this;

    self.Campaign = null;

    function calculateEstimatedTotalCostForMessage(
        messageCount,
        costPerMessage,
        messageParts) {
        return messageCount * (costPerMessage * messageParts);
    }

    function messageContainsPush(message) {
        const messageContent =
            message.getPushNotificationMessagePart(Dashboard.PushNotificationMessagePart.PushNotification);
        return !!messageContent;
    }

    function messageContainsInbox(message) {
        const messageContent =
            message.getPushNotificationMessagePart(Dashboard.PushNotificationMessagePart.InboxBlurb);
        return !!messageContent;
    }

    /**
     * This function adds a method on communications to retrieve their push
     * notification and inbox communication content that is displayed on the
     * UI with the replaced placeholders
     */
    function addGetPushNotificationMessagePartOnMessage(message) {
        if (typeof message.getPushNotificationMessagePart === "function") {
            return;
        }
        message.getPushNotificationMessagePart = pushNotificationMessagePart => {
            switch (pushNotificationMessagePart) {
                case Dashboard.PushNotificationMessagePart.PushNotification:
                    return message.PushNotificationContent.PushNotificationMessage;
                case Dashboard.PushNotificationMessagePart.InboxBlurb:
                    if (!message.PushNotificationContent
                        || !message.PushNotificationContent.InboxMessageProperties) {
                        return "";
                    }
                    return message.PushNotificationContent.InboxMessageProperties.Blurb;
                case Dashboard.PushNotificationMessagePart.InboxTemplate:
                    if (!message.PushNotificationContent
                        || !message.PushNotificationContent.InboxMessageProperties) {
                        return "";
                    }
                    return message.PushNotificationContent.InboxMessageProperties.Template;
            }
        }
    }

    function calculateCostForCommunication(communication, campaign) {
        communication.EstimatedTotalCost = 0;
        communication.EstimatedTotalSMSCustomers = 0;
        communication.EstimatedTotalPushCustomers = 0;
        _.each(communication.Messages, function (message) {
            message.SMSCost = 0;
            message.PushCost = 0;
            let shouldPushIfPossible = false;
            let shouldSMSIfPossible = false;
            const isSMS = message.Type === Dashboard.Models.Lookups.CommunicationMessageTypes.SMS;
            const isPush = message.Type === Dashboard.Models.Lookups.CommunicationMessageTypes.PushNotification;
            if (message.ExclusionRule) {
                shouldPushIfPossible = JSON.parse(message.ExclusionRule).CanPush;
                shouldSMSIfPossible = JSON.parse(message.ExclusionRule).CanSMS;
            }

            const proportionCustomers = campaign.SelectedCustomerCount / campaign.MaxCustomers;
            let pushCustomers = isPush ? campaign.CustomersOptedInForPush : 0;
            let smsCustomers = isSMS ? campaign.CustomersOptedInForSMS : 0;

            if (isSMS && shouldPushIfPossible) {
                smsCustomers = campaign.CustomersOptedInForSMSOnly;
            }

            if (isPush && shouldSMSIfPossible) {
                pushCustomers = campaign.CustomersOptedInForPushOnly;
            }

            if (communication.SMSDisabled) {
                smsCustomers = 0;
            }

            if (campaign.SelectedCustomerCount === 0) {
                message.SMSCustomers = 0;
                message.PushCustomers = 0;
            } else {
                message.PushCustomers = Math.round(pushCustomers * proportionCustomers);
                message.SMSCustomers = Math.round(smsCustomers * proportionCustomers);
            }

            if (isSMS) {
                communication.HasSMS = true;
            }

            if (isPush) {
                communication.HasPush = true;
            }

            message.SMSCost = calculateEstimatedTotalCostForMessage(
                message.SMSCustomers,
                campaign.CostPerSMS,
                message.MessageParts);

            message.PushCost = calculateEstimatedTotalCostForMessage(
                message.PushCustomers,
                campaign.CostPerPush,
                1);

            communication.EstimatedTotalSMSCustomers += message.SMSCustomers;
            communication.EstimatedTotalPushCustomers += message.PushCustomers;
            communication.EstimatedTotalCost += message.SMSCost + message.PushCost;
        });
    }

    self.HasVoucherOverride = function (campaign) {
        const hasVouchers = self.HasVouchers(campaign);

        if (!hasVouchers) {
            return false;
        }

        const expiryDaysOverride = campaign.TemplateActions.Vouchers[0].ExpiryDaysOverride;
        return expiryDaysOverride && expiryDaysOverride > 0;
    };

    self.HasVouchers = function (campaign) {
        if (!campaign || !campaign.TemplateActions) {
            return false;
        }
        return _.size(campaign.TemplateActions.Vouchers) > 0;
    };

    self.HasCommunications = function (campaign) {
        if (!campaign || !campaign.TemplateActions) {
            return false;
        }

        return _.size(campaign.TemplateActions.Communications) > 0;
    };

    self.fetchSMSMessage = function (communication) {
        if (!communication?.Messages.length) {
            return null;
        }
        return _.findWhere(
            communication.Messages,
            {
                CommunicationTypeId: Dashboard.CommunicationMessageType.SMS
            }
        );
    };

    self.HasSMSMessage = function (communication) {
        const hasSMS = self.fetchSMSMessage(communication);
        return !!hasSMS;
    };

    self.HasInboxMessage = function (communication) {
        const inboxMessage = _.findWhere(
            communication.Messages,
            {
                CommunicationTypeId: Dashboard.CommunicationMessageType.PushNotification
            }
        );
        return !!inboxMessage;
    };

    self.HasPushMessage = function (communication) {
        const pushMessage = _.findWhere(
            communication.Messages,
            {
                CommunicationTypeId: Dashboard.CommunicationMessageType.PushNotification
            }
        );
        if (!pushMessage) {
            return false;
        }

        return messageContainsPush(pushMessage);
    };

    self.GetCommunicationTypeTags = function (communication) {
        const tags = [];
        communication.Messages.forEach(function (message) {
            if (message.CommunicationTypeId === Dashboard.CommunicationMessageType.SMS) {
                tags.push("SMS");
            }

            if (message.CommunicationTypeId === Dashboard.CommunicationMessageType.PushNotification) {
                if (messageContainsPush(message)) {
                    tags.push("Push");
                }

                if (messageContainsInbox(message)) {
                    tags.push("Inbox");
                }
            }
        });

        return tags;
    };

    /**
     * Checks if the date passed in is available based on the collection of available dates passed in
     * @param dateToCheck {Date}
     * @param availableDates {Array}
     * @returns {boolean}
     */
    self.CheckIfDateAvailable = function (dateToCheck, availableDates) {
        return _.some(availableDates, function (availableDate) {
            return moment(dateToCheck).isSame(availableDate, 'day');
        });
    };

    self.FetchNonWaspaCommunicationsDateTimeRule = function () {
        const ruleString = DashboardConfig.NonWaspaCommunicationsDateTimeRule;
        const defaultRule = {
            From: "8:00",
            To: "20:00"
        }
        if (!ruleString) {
            return defaultRule;
        }
        let dateTimeRule = ValidateTimeRule(ruleString);
        return dateTimeRule ?
            dateTimeRule :
            defaultRule;
    }

    function ValidateTimeRule(dateTimeRuleString) {
        let rule;
        try {
            rule = JSON.parse(`${dateTimeRuleString}`.replace(/'/g, "\""));
        } catch (e) {
            console.warn(
                `Invalid JSON format for rule: '${dateTimeRuleString}'`
            );
            return null;
        }
        if (!rule || !rule.From || !rule.To) {
            console.warn(
                new Error("Invalid time format in time rule"),
                dateTimeRuleString);
            return null;
        }
        const currentDate = moment().format(moment.HTML5_FMT.DATE);
        const validTime = /^([0-1]?[0-9]|2[0-4]):([0-5][0-9])(:[0-5][0-9])?$/;
        const inputIsInvalid = !validTime.test(rule.From) || !validTime.test(rule.To);
        const fromTimeIsAfterToTime = Date.parse(`${currentDate} ${rule.From}`)
            > Date.parse(`${currentDate} ${rule.To}`);
        if (inputIsInvalid || fromTimeIsAfterToTime) {
            console.warn(
                new Error("Invalid time format in time rule"),
                dateTimeRuleString);
            return null;
        }
        return rule;
    }

    self.checkIfSMSCommsAreAllowed = function (comms) {
        const smsComms = self.fetchSMSMessage(comms);
        const waspaCommsDateTimeRules = _.findWhere(
            comms.DayOfWeekMarketingRules.TimeRules,
            { Day: comms.PublishDate.getDay() }
        );
        const dateIsWithinWaspaDates = self.CheckIfDateAvailable(
            comms.PublishDate,
            comms.WaspaCommunicationDates);
        if (!dateIsWithinWaspaDates) {
            comms.SMSDisabled = true;
            smsComms.Disabled = true;
            return;
        }
        comms.SMSDisabled = false;
        smsComms.Disabled = false;
        const selectedDate = moment(comms.PublishDate).format(moment.HTML5_FMT.DATE);
        const selectedTime = moment(comms.PublishTime).format(moment.HTML5_FMT.TIME);
        const selectedDateTime = Date.parse(`${selectedDate} ${selectedTime}`);
        const timeIsWithinWaspaTimes = selectedDateTime >= Date.parse(`${selectedDate} ${waspaCommsDateTimeRules.From}`)
            && selectedDateTime <= Date.parse(`${selectedDate} ${waspaCommsDateTimeRules.To}`);
        if (!timeIsWithinWaspaTimes) {
            comms.SMSDisabled = true;
            smsComms.Disabled = true;
        }
    };

    /**
     * Initialises the communication dates and times for the campaign
     */
    self.InitialiseCommunications = function (
        communications,
        localisationService) {
        _.each(communications, function (comms) {
            comms.setDisabledMessageDates = function (data) {
                const date = data.date,
                    mode = data.mode;
                return mode === 'day' && !self.CheckIfDateAvailable(date, comms.AvailableMessageDates);
            };

            /**
             * This function is fired if the specific comms publish date changes. This sets the min/max times available
             */
            comms.commsPublishDateChanged = function (callback) {
                if (self.HasSMSMessage(comms)) {
                    self.checkIfSMSCommsAreAllowed(comms);
                }
                const dateTimeRule = self.FetchNonWaspaCommunicationsDateTimeRule();
                if (dateTimeRule) {
                    comms.minTime = localisationService.combineDateTime(
                        comms.PublishDate,
                        dateTimeRule.From);
                    let now = new Date();
                    // If the date is today and the earliest start time is earlier than now,
                    // then set the earliest start time to now
                    if (comms.minTime < now) {
                        now = now.setMinutes(5 * Math.ceil(now.getMinutes() / 5));
                        comms.minTime = localisationService.combineDateTime(
                            comms.PublishDate,
                            LocalisationService.formatDateTime(now, "HH:mm")
                        );
                    }
                    comms.maxTime = localisationService.combineDateTime(
                        comms.PublishDate,
                        dateTimeRule.To);
                    $timeout(function () {
                        comms.PublishTime = comms.minTime;
                    }, 0);
                }
                callback();
            };

            comms.commsTimeChanged = function (callback) {
                if (self.HasSMSMessage(comms)) {
                    self.checkIfSMSCommsAreAllowed(comms);
                }
                callback();
            };

            comms.messageDateOptions = {
                formatYear: 'yy',
                startingDay: 1,
                showWeeks: false,
                dateDisabled: comms.setDisabledMessageDates,
                showDatePicker: false
            };

            // Shows the message date picker
            comms.openMessageDatePicker = () => {
                comms.messageDateOptions.showDatePicker = true;
            };

            // Add a function on communication messages to retrieve their push notification and inbox communication content that is displayed on the UI with the replaced placeholders
            _.each(comms.Messages, function (message) {
                addGetPushNotificationMessagePartOnMessage(message);
            });
        });
    };

    self.CalculateCommunicationsCosts = function (
        communications,
        campaign) {
        campaign.TotalCommunicationsCost = 0;
        _.each(communications, function (comms) {
            calculateCostForCommunication(comms, campaign);
            comms.EffectiveCostPerMessage = LocalisationService.formatCurrency(
                comms.EstimatedTotalCost / (comms.EstimatedTotalSMSCustomers + comms.EstimatedTotalPushCustomers));
            comms.FormattedTotalCost = LocalisationService.formatCurrency(comms.EstimatedTotalCost);
            campaign.TotalCommunicationsCost += comms.EstimatedTotalCost;
        });
    };

    /**
     * Check if the user has access to view the Users page
     */
    self.CheckUserAccessToCampaignManagement = function () {
        const canUserManageCampaigns = !PermissionService.userContext
            || !PermissionService.doesUserHavePermissionForContext(
                Dashboard.Models.Lookups.Permissions.ManageCampaigns,
                PermissionService.userContext);
        if (canUserManageCampaigns) {
            PermissionService.navigateToAccessDenied();
        }
    };
}
